import * as React from "react";

import {
  AgentThreadMessage,
  ClientViewingContext,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import { scanForShifts, type ScanState } from "./contextShift";

interface Checkpoint {
  processedCount: number;
  endState: ScanState;
  shifts: Map<number, ClientViewingContext>;
}

/**
 * Hook that incrementally detects context shifts in a message list.
 *
 * On each render where new messages have been appended, only the new
 * messages are scanned — previously-detected shifts are reused from
 * a cached checkpoint. This avoids O(n) re-scans on every keystroke
 * in long conversations.
 *
 * Falls back to a full re-scan when messages shrink (e.g. thread changed).
 */
export function useContextShifts(
  messages: AgentThreadMessage[],
): Map<number, ClientViewingContext> {
  const checkpointRef = React.useRef<Checkpoint | null>(null);

  return React.useMemo(() => {
    const checkpoint = checkpointRef.current;

    // Can we resume from the checkpoint?
    if (checkpoint && messages.length >= checkpoint.processedCount) {
      // Only scan messages we haven't seen yet
      const { shifts: newShifts, endState } = scanForShifts(
        messages,
        checkpoint.processedCount,
        checkpoint.endState,
      );

      if (newShifts.size === 0) {
        // No new shifts — update processedCount but keep same Map reference
        checkpointRef.current = {
          processedCount: messages.length,
          endState,
          shifts: checkpoint.shifts,
        };
        return checkpoint.shifts;
      }

      // Merge new shifts into a fresh Map
      const merged = new Map([...checkpoint.shifts, ...newShifts]);
      checkpointRef.current = {
        processedCount: messages.length,
        endState,
        shifts: merged,
      };
      return merged;
    }

    // Full re-scan (first render or messages array shrank)
    const { shifts, endState } = scanForShifts(messages, 0, {
      previousContext: null,
    });
    checkpointRef.current = {
      processedCount: messages.length,
      endState,
      shifts,
    };
    return shifts;
  }, [messages]);
}
